import React, { useCallback, useMemo, useState } from "react";
import { FiRefreshCcw, FiDownload, FiFilter } from "react-icons/fi";
import Divider from "../../../../components/shared/Divider/Divider";
import { Pagination } from "../../../../components/shared/Pagination/Pagination";
import { UserFiltersPanel } from "./UserFiltersPanel";
import Button from "../../../../components/shared/Button/Button";
import SelectMulti from "../../../../components/shared/Select/SelectMulti";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { useAdminSearchUsers } from "../../hooks/useAdminSearchUsers";
import { useCsvExport } from "../../hooks/useCsvExport";
import { USERS_FIXED_COLUMNS, usersColumnOptions } from "./constants/columns";
import { createUsersColumnDefinitions } from "./UserTableColumns";
import EditUserModal from "./EditUserModal";
import { useInvalidate } from "../../../../hooks/useInvalidate";

export default function UsersView() {
  const invalidate = useInvalidate();
  const {
    usersState,
    setUsersPagination,
    setUsersVisibleColumns,
    setUsersFiltersOpen,
    setUsersSelectedUserId,
  } = useAdminSearchStore();

  const { filters, pagination, visibleColumns, filtersOpen, selectedUserId } =
    usersState;

  const { data, isLoading, error } = useAdminSearchUsers();
  const users = data?.users || [];
  const total = data?.total || 0;
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / (pagination.limit || 1))),
    [total, pagination.limit]
  );

  const handleSearchSubmit = useCallback(() => {
    setUsersPagination({ page: 1 });
    setUsersFiltersOpen(false);
    invalidate(["adminSearchUsers_key"]);
  }, [invalidate, setUsersFiltersOpen, setUsersPagination]);

  const onPageChange = useCallback(
    async (page) => {
      const clamped = Math.min(Math.max(page, 1), totalPages);
      setUsersPagination({ page: clamped });
    },
    [totalPages, setUsersPagination]
  );

  const onRowsPerPageChange = useCallback(
    async (n) => {
      setUsersPagination({ page: 1, limit: n });
    },
    [setUsersPagination]
  );

  const onRefresh = useCallback(() => {
    invalidate(["adminSearchUsers_key"]);
  }, [invalidate]);

  // Column definitions
  const columnDefinitions = useMemo(() => createUsersColumnDefinitions(), []);

  const visibleColumnDefs = useMemo(() => {
    // Always include the fixed columns first
    const fixedCols = USERS_FIXED_COLUMNS.map(
      (colKey) => columnDefinitions[colKey]
    ).filter(Boolean);
    const otherCols = visibleColumns
      .filter(
        (colKey) =>
          !USERS_FIXED_COLUMNS.includes(colKey) && columnDefinitions[colKey]
      )
      .map((colKey) => columnDefinitions[colKey]);
    // Return fixed columns first, then other selected columns
    return [...fixedCols, ...otherCols];
  }, [visibleColumns, columnDefinitions]);

  const columnCount = visibleColumnDefs.length;

  const hasActiveFilters = useMemo(() => {
    if (filters.userInput?.trim()) return true;
    if (
      Array.isArray(filters.subscriptionType) &&
      filters.subscriptionType.length > 0
    )
      return true;
    if (filters.gender) return true;
    if (filters.dominantHand) return true;
    if (filters.secondarySubType) return true;
    if (filters.userCreated?.from || filters.userCreated?.to) return true;
    if (
      filters.studentCredits?.min != null ||
      filters.studentCredits?.max != null
    )
      return true;
    if (
      filters.reportCredits?.min != null ||
      filters.reportCredits?.max != null
    )
      return true;
    return false;
  }, [filters]);

  const { exporting, exportCsv } = useCsvExport({
    filters,
    visibleColumnDefs,
  });

  const handleEditUser = useCallback(
    (userId) => {
      setIsEditingModalOpen(true);
      setUsersSelectedUserId(userId);
    },
    [setIsEditingModalOpen, setUsersSelectedUserId]
  );

  const handleCloseEdit = useCallback(() => {
    setIsEditingModalOpen(false);
    setUsersSelectedUserId(null);
  }, [setIsEditingModalOpen, setUsersSelectedUserId]);

  const columnHandlers = useMemo(
    () => ({
      onEditUser: handleEditUser,
    }),
    [handleEditUser]
  );

  return (
    <>
      <div className="mb-4">
        <div className="flex flex-wrap items-stretch gap-3">
          {" "}
          <Button
            type="button"
            onClick={() => setUsersFiltersOpen(true)}
            color={hasActiveFilters ? "primary50" : "white/gray300"}
            size="md"
            rounded="lg"
            startContent={<FiFilter />}
            title="Open filters"
          >
            Filters
          </Button>
          <SelectMulti
            header="Columns"
            className="min-w-[192px]"
            onChange={(val) => {
              // Prevent toggling the fixed columns
              if (USERS_FIXED_COLUMNS.includes(val)) return;
              setUsersVisibleColumns((prev) => {
                const filtered = prev.filter(
                  (v) => !USERS_FIXED_COLUMNS.includes(v)
                );
                return filtered.includes(val)
                  ? filtered.filter((v) => v !== val)
                  : [...filtered, val];
              });
            }}
            onClear={() => setUsersVisibleColumns([...USERS_FIXED_COLUMNS])}
            options={usersColumnOptions}
            placeholder="Select columns..."
            values={visibleColumns.filter(
              (v) => !USERS_FIXED_COLUMNS.includes(v)
            )}
            buttonProps={{
              size: "md",
              rounded: "lg",
              color:
                visibleColumns.filter((v) => !USERS_FIXED_COLUMNS.includes(v))
                  .length > 0
                  ? "primary50"
                  : "white/gray300",
            }}
          />
          <Button
            type="button"
            isDisabled={exporting || isLoading}
            onClick={onRefresh}
            color="white/gray300"
            size="md"
            rounded="lg"
            startContent={<FiRefreshCcw />}
            title="Refresh"
          >
            Refresh
          </Button>
          <Button
            type="button"
            onClick={exportCsv}
            isDisabled={exporting || isLoading}
            isLoading={exporting}
            color="white/gray300"
            size="md"
            rounded="lg"
            startContent={<FiDownload />}
            className="whitespace-nowrap"
            title="Export all results to CSV"
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </div>

      <UserFiltersPanel
        isOpen={filtersOpen}
        onClose={() => setUsersFiltersOpen(false)}
        onSubmit={handleSearchSubmit}
      />

      <div className="overflow-hidden rounded-xl border border-gray200">
        <div className="overflow-x-auto min-h-80">
          <table className="min-w-full text-left text-base">
            <thead className="bg-gray50 text-sm uppercase tracking-wide text-gray600">
              <tr>{visibleColumnDefs.map((col) => col.renderHeader())}</tr>
            </thead>

            <tbody className="divide-y divide-gray200">
              {error && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-red-700"
                  >
                    {error?.message || "Failed to fetch users"}
                  </td>
                </tr>
              )}
              {isLoading && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-gray500"
                  >
                    Loading…
                  </td>
                </tr>
              )}
              {!isLoading && users.length === 0 && !error && (
                <tr>
                  <td
                    colSpan={columnCount}
                    className="px-6 py-10 text-center text-base text-gray500"
                  >
                    No results
                  </td>
                </tr>
              )}

              {users.map((u, index) => {
                return (
                  <tr key={u.id} className={index % 2 === 1 ? "bg-gray50" : ""}>
                    {visibleColumnDefs.map((col) => (
                      <React.Fragment key={col.key}>
                        {col.renderCell(u, columnHandlers)}
                      </React.Fragment>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <Divider color="gray200" type="horizontal" />

        <Pagination
          currentPage={pagination.page}
          pageNumber={totalPages}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPage={pagination.limit}
          showItemsPerPage={users.length > 0}
          size="lg"
          className="px-6 py-4"
        />
      </div>

      <EditUserModal isOpen={isEditingModalOpen} onClose={handleCloseEdit} />
    </>
  );
}
